import chunk from 'lodash/chunk';
import { useCreatorExploreListQueries } from '@round/creatorbase-helpers/api-hooks';
import { useInstagramUserImageListQueries } from '@round/platforms/instagram';
import { useTikTokUserImagesListQueries } from '@round/platforms/tiktok';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { getAccountUserIds, getExploreClientInfluencerIds, getImageUrl, withExploreAccountDetails } from '../helpers';
import { CreatorAccountRef } from '../types';

// How many creators each Creator Explore lookup asks for, which keeps their UUIDs well within a request URL.
const EXPLORE_CHUNK_SIZE = 25;

// How many accounts each image lookup asks for, chunked the same way as the Explore lookups they follow.
const IMAGES_CHUNK_SIZE = 25;

/**
 * Each of the accounts paired with itself as Creator Explore has it, and with its image, which is what
 * the accounts table shows them by. The accounts come back as they were given, with the two added.
 */
export function useCreatorAccountRows<T extends CreatorAccountRef>(accounts: T[]) {
    const { selectedClientId } = useClientSelect();
    const hasClient = typeof selectedClientId === 'number';

    // The creators are looked up in chunks rather than all at once, since a whole upload's worth of UUIDs
    // would not fit in a request URL. Each lookup asks for a page the size of its chunk so none of them is
    // left on a page nobody fetches. The rate Creator Explore answers with is the selected client's own,
    // so nothing is asked for until there is one.
    const explore = useCreatorExploreListQueries(
        chunk(getExploreClientInfluencerIds(accounts), EXPLORE_CHUNK_SIZE).map((ids) => ({
            params: {
                client_id: hasClient ? selectedClientId : undefined,
                client_influencer_id: ids.join(','),
                page_size: ids.length,
            },
            config: { enabled: ids.length > 0 && hasClient },
        })),
        {
            combine: (queries) => ({
                results: queries.flatMap((query) => query.data?.results ?? []),
                isLoading: queries.some((query) => query.isLoading),
            }),
        }
    );

    // An account's image is looked up by the platform user Creator Explore pairs it with, so these wait on
    // Explore and ask only for the accounts given, not every account of their creators.
    const tiktokUserIds = getAccountUserIds(accounts, explore.results, 'tiktok');
    const instagramUserIds = getAccountUserIds(accounts, explore.results, 'instagram');

    const tiktokImages = useTikTokUserImagesListQueries(
        chunk(tiktokUserIds, IMAGES_CHUNK_SIZE).map((ids) => ({ params: { ids } })),
        {
            combine: (queries) => ({
                urlsByUserId: new Map(
                    queries.flatMap((query) => query.data ?? []).map((image) => [image.user_id, getImageUrl(image)])
                ),
                isLoading: queries.some((query) => query.isLoading),
            }),
        }
    );

    const instagramImages = useInstagramUserImageListQueries(
        chunk(instagramUserIds, IMAGES_CHUNK_SIZE).map((ids) => ({ params: { ids } })),
        {
            combine: (queries) => ({
                urlsByUserId: new Map(
                    queries.flatMap((query) => query.data ?? []).map((image) => [image.user_id, getImageUrl(image)])
                ),
                isLoading: queries.some((query) => query.isLoading),
            }),
        }
    );

    return {
        rows: withExploreAccountDetails(accounts, explore.results, {
            tiktok: tiktokImages.urlsByUserId,
            instagram: instagramImages.urlsByUserId,
        }),
        isExploreLoading: explore.isLoading,
        isImagesLoading: tiktokImages.isLoading || instagramImages.isLoading,
    };
}
